import type { ControllerRenderProps } from 'react-hook-form';
import { Checkbox } from '../../primitives/checkbox.js';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from '../../primitives/form.js';
import { Input } from '../../primitives/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../primitives/select.js';
import { Switch } from '../../primitives/switch.js';
import { Textarea } from '../../primitives/textarea.js';
import { cn } from '../../lib/cn.js';
import { RelationPicker } from '../filters/relation-picker.js';
import type { ResourceFormField } from '../types.js';

/** JSON attribute bags are edited as text; the schema is what validates them. */
function jsonToText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
}

export interface KitFormFieldProps {
  field: ResourceFormField;
  disabled: boolean;
}

/**
 * A custom control, given the id its label points at.
 *
 * It is a component rather than an inline branch because `useFormField` has to
 * run inside the `FormItem` that generates that id.
 */
function CustomControl({
  field,
  control,
  disabled,
}: {
  field: Extract<ResourceFormField, { type: 'custom' }>;
  control: ControllerRenderProps;
  disabled: boolean;
}) {
  const { formItemId } = useFormField();

  return (
    <div>
      {field.render({
        name: control.name,
        value: control.value,
        onChange: control.onChange,
        onBlur: control.onBlur,
        disabled,
        id: formItemId,
      })}
    </div>
  );
}

/**
 * A select, with what `FormControl` hands its child — the id the label points
 * at, and the description and error wiring — put on the trigger.
 *
 * Radix's `Select` root renders no element, so given those props directly it
 * drops them: the trigger would be a combobox with no name, and an invalid
 * value would not be announced as one.
 */
function SelectControl({
  field,
  control,
  disabled,
  ...slotProps
}: {
  field: Extract<ResourceFormField, { type: 'select' }>;
  control: ControllerRenderProps;
  disabled: boolean;
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}) {
  return (
    <Select
      value={typeof control.value === 'string' ? control.value : ''}
      disabled={disabled}
      onValueChange={(value) => control.onChange(value)}
    >
      <SelectTrigger {...slotProps}>
        <SelectValue placeholder={field.placeholder ?? 'Seleziona…'} />
      </SelectTrigger>
      <SelectContent>
        {field.options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.icon}
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function KitFormField({ field, disabled: formDisabled }: KitFormFieldProps) {
  const disabled = formDisabled || (field.disabled ?? false);

  return (
    <FormField
      name={field.name}
      // `col-span-full` rather than `col-span-2`: a wide field has to span
      // three columns as well, now that sections can ask for three.
      render={({ field: control }) => (
        <FormItem className={cn(field.wide && 'sm:col-span-full')}>
          {field.type !== 'checkbox' && field.type !== 'switch' && (
            <FormLabel>
              {field.label}
              {field.required && <span className="text-danger">*</span>}
            </FormLabel>
          )}

          <FormControl>
            {(() => {
              switch (field.type) {
                case 'text':
                case 'email':
                case 'tel':
                case 'url':
                case 'password':
                  return (
                    <Input
                      type={field.type}
                      placeholder={field.placeholder}
                      disabled={disabled}
                      {...control}
                      value={typeof control.value === 'string' ? control.value : ''}
                    />
                  );

                case 'date':
                  return (
                    <Input
                      type="date"
                      disabled={disabled}
                      {...control}
                      value={typeof control.value === 'string' ? control.value : ''}
                    />
                  );

                case 'number':
                  return (
                    <Input
                      type="number"
                      min={field.min}
                      max={field.max}
                      step={field.step}
                      placeholder={field.placeholder}
                      disabled={disabled}
                      name={control.name}
                      ref={control.ref}
                      onBlur={control.onBlur}
                      value={typeof control.value === 'number' ? control.value : ''}
                      onChange={(event) =>
                        control.onChange(
                          event.target.value === '' ? null : Number(event.target.value),
                        )
                      }
                    />
                  );

                case 'textarea':
                  return (
                    <Textarea
                      rows={field.rows ?? 4}
                      placeholder={field.placeholder}
                      disabled={disabled}
                      {...control}
                      value={typeof control.value === 'string' ? control.value : ''}
                    />
                  );

                case 'json':
                  return (
                    <Textarea
                      rows={field.rows ?? 8}
                      spellCheck={false}
                      disabled={disabled}
                      className="font-mono text-secondary"
                      name={control.name}
                      ref={control.ref}
                      onBlur={control.onBlur}
                      value={jsonToText(control.value)}
                      onChange={(event) => control.onChange(event.target.value)}
                    />
                  );

                case 'checkbox':
                  return (
                    <label className="flex items-center gap-2 text-body text-ink-2">
                      <Checkbox
                        checked={control.value === true}
                        disabled={disabled}
                        onCheckedChange={(checked) => control.onChange(checked === true)}
                      />
                      {field.label}
                    </label>
                  );

                case 'switch':
                  return (
                    <label className="flex items-center gap-2 text-body text-ink-2">
                      <Switch
                        checked={control.value === true}
                        disabled={disabled}
                        onCheckedChange={(checked) => control.onChange(checked)}
                      />
                      {field.label}
                    </label>
                  );

                case 'select':
                  return <SelectControl field={field} control={control} disabled={disabled} />;

                case 'multiSelect': {
                  // A checkbox list rather than a multi-value combobox: these
                  // sets are short and named, and seeing all of them at once is
                  // the point (a contact's classifications, a property's
                  // features).
                  const selected = Array.isArray(control.value) ? (control.value as string[]) : [];
                  return (
                    <div className="grid gap-2 sm:grid-cols-2">
                      {field.options.map((option) => (
                        <label
                          key={option.value}
                          className="flex items-center gap-2 text-body text-ink-2"
                        >
                          <Checkbox
                            checked={selected.includes(option.value)}
                            disabled={disabled}
                            onCheckedChange={(checked) =>
                              control.onChange(
                                checked === true
                                  ? [...selected, option.value]
                                  : selected.filter((value) => value !== option.value),
                              )
                            }
                          />
                          {option.icon}
                          {option.label}
                        </label>
                      ))}
                    </div>
                  );
                }

                case 'relation':
                  return (
                    <RelationPicker
                      value={typeof control.value === 'string' ? control.value : null}
                      onChange={(value) => control.onChange(value)}
                      search={field.search}
                      resolve={field.resolve}
                      placeholder={field.placeholder}
                      disabled={disabled}
                    />
                  );

                case 'custom':
                  return <CustomControl field={field} control={control} disabled={disabled} />;
              }
            })()}
          </FormControl>

          {field.description && <FormDescription>{field.description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
